import React, { createContext, useContext, useState, useEffect } from 'react';
import SanityAPI from '../utils/sanityAPI';

const DataContext = createContext();

export function useData() {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useData must be used within DataProvider');
    }
    return context;
}

// LocalStorage keys
const CACHE_KEYS = {
    PRODUCTS: 'sanity_products_cache',
    CATEGORIES: 'sanity_categories_cache',
    TIMESTAMP: 'sanity_cache_timestamp'
};

// Cache helpers
const saveToCache = (key, data) => {
    try {
        localStorage.setItem(key, JSON.stringify(data));
        localStorage.setItem(CACHE_KEYS.TIMESTAMP, Date.now().toString());
        console.log(`💾 Cached ${key} to localStorage`);
    } catch (e) {
        console.error('❌ Failed to save to cache:', e);
    }
};

const loadFromCache = (key) => {
    try {
        const cached = localStorage.getItem(key);
        if (cached) {
            console.log(`📦 Loaded ${key} from cache`);
            return JSON.parse(cached);
        }
    } catch (e) {
        console.error('❌ Failed to load from cache:', e);
        localStorage.removeItem(key);
    }
    return null;
};

export function DataProvider({ children }) {
    // Initialize with cached data if available
    const [products, setProducts] = useState(() => loadFromCache(CACHE_KEYS.PRODUCTS) || []);
    const [categories, setCategories] = useState(() => loadFromCache(CACHE_KEYS.CATEGORIES) || []);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [usingSanity, setUsingSanity] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        console.log('🔄 DataContext: Loading data from Sanity...');
        setLoading(true);

        try {
            // Try to fetch from Sanity
            const [sanityProducts, sanityCategories] = await Promise.all([
                SanityAPI.getProducts(),
                SanityAPI.getCategories()
            ]);

            // Check if we got valid data
            if (sanityProducts && sanityProducts.length > 0) {
                console.log(`✅ DataContext: Using Sanity products (${sanityProducts.length} items)`);
                setProducts(sanityProducts);
                saveToCache(CACHE_KEYS.PRODUCTS, sanityProducts);
                setUsingSanity(true);
            } else {
                console.warn('⚠️ DataContext: No products from Sanity, using cache');
                const cached = loadFromCache(CACHE_KEYS.PRODUCTS);
                setProducts(cached || []);
                setUsingSanity(false);
            }

            if (sanityCategories && sanityCategories.length > 0) {
                console.log(`✅ DataContext: Using Sanity categories (${sanityCategories.length} items)`);
                setCategories(sanityCategories);
                saveToCache(CACHE_KEYS.CATEGORIES, sanityCategories);
            } else {
                console.warn('⚠️ DataContext: No categories from Sanity, using cache');
                const cached = loadFromCache(CACHE_KEYS.CATEGORIES);
                setCategories(cached || []);
            }

            setError(null);
        } catch (err) {
            console.error('❌ DataContext: Failed to load from Sanity, using cached data');
            console.error('Error details:', err);

            // Fallback to cached data
            const cachedProducts = loadFromCache(CACHE_KEYS.PRODUCTS);
            const cachedCategories = loadFromCache(CACHE_KEYS.CATEGORIES);

            setProducts(cachedProducts || []);
            setCategories(cachedCategories || []);
            setError(err.message);
            setUsingSanity(false);
        } finally {
            setLoading(false);
        }
    };

    const refreshData = () => {
        loadData();
    };

    const value = {
        products,
        categories,
        loading,
        error,
        usingSanity,
        refreshData
    };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
}

export default DataContext;
